import { useEffect, useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { Sidebar } from "@/shell/Sidebar";
import { TopBar } from "@/shell/TopBar";

interface DashboardLayoutProps {
  titleId: string;
  children: ReactNode;
}

// Matches Tailwind's default `md` breakpoint (768px) — Sidebar/main below use
// the same cutoff for "is this a desktop layout" via `md:` classes, so the
// initial state has to agree with that or the sidebar opens full-width over
// mobile content (or collapses on desktop) for one render before anyone
// touches it.
function isDesktopViewport(): boolean {
  return typeof window !== "undefined" && window.innerWidth >= 768;
}

export function DashboardLayout({ titleId, children }: DashboardLayoutProps) {
  const intl = useIntl();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktopViewport);

  // Same titleId TopBar already resolves for its own heading — reused here
  // so the browser tab always names the active sidebar section, not the
  // static "LendCord App" from index.html.
  useEffect(() => {
    document.title = `${intl.formatMessage({ id: titleId })} · LendCord`;
    return () => {
      document.title = "LendCord App";
    };
  }, [intl, titleId]);

  return (
    <div className="min-h-dvh bg-background">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* No offset on mobile — Sidebar is an off-canvas overlay there, never
          part of the flow. md:ml-* mirrors Sidebar's own md:w-20/md:w-60. */}
      <main className={sidebarOpen ? "md:ml-60" : "md:ml-20"}>
        <TopBar titleId={titleId} onToggleSidebar={() => setSidebarOpen((p) => !p)} />
        {children}
      </main>
    </div>
  );
}
